import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { Row as RowType } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorActions, useEditorStore } from '@/store/editor.store';

import {
  FreeformColumnSurface,
  FreeformRow,
  RowSharedHeightResizeBar,
  rowHeightPx,
} from './FreeformRow';

interface Props {
  row: RowType;
  slideId: string;
}

export function Row({ row, slideId }: Props) {
  const sortable = useSortable({
    id: row.id,
    data: { source: 'canvas', kind: 'row', slideId, rowId: row.id },
  });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  const isSelected = useEditorStore(
    (s) => s.selection?.kind === 'row' && s.selection.rowId === row.id,
  );
  const actions = useEditorActions();

  // Every column is a freeform canvas now. Single-column rows render one
  // surface (with its own resize handle); multi-column rows render one surface
  // per column side by side, sharing a single height (resized via the bar).
  const multiColumn = row.columns.length > 1;
  const [liveRowHeightPx, setLiveRowHeightPx] = useState<number | null>(null);
  const sharedHeightPx = liveRowHeightPx ?? rowHeightPx(row);

  const onSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    useEditorStore.getState().setSelection({ kind: 'row', slideId, rowId: row.id });
  };

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    minHeight: 80,
    paddingTop: row.padding.top,
    paddingRight: row.padding.right,
    paddingBottom: row.padding.bottom,
    paddingLeft: row.padding.left,
  };

  return (
    <div
      ref={setNodeRef}
      onClick={onSelect}
      style={style}
      className={cn(
        'group/row relative flex w-full cursor-default rounded-md transition',
        isSelected && 'ring-1 ring-accent/50',
        isDragging && 'opacity-50',
      )}
    >
      {/* drag handle — sits INSIDE the slide edge so it isn't clipped by the
          slide's overflow:hidden container. */}
      <div
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        className="absolute left-1 top-1/2 z-20 grid h-7 w-7 -translate-y-1/2 cursor-grab place-items-center rounded-md border border-border bg-white text-fg-subtle opacity-0 shadow-pill transition group-hover/row:opacity-100 active:cursor-grabbing"
        title="Drag to reorder"
      >
        <GripVertical size={14} />
      </div>

      {/* row actions */}
      <div className="absolute right-1 top-1 z-20 flex flex-col gap-1 opacity-0 transition group-hover/row:opacity-100">
        <button
          onClick={(e) => {
            e.stopPropagation();
            useEditorStore.getState().setSelection({ kind: 'row', slideId, rowId: row.id });
            actions.duplicateSelection();
          }}
          className="grid h-6 w-6 place-items-center rounded-md border border-border bg-white text-fg-subtle shadow-pill hover:text-fg"
          title="Duplicate row"
        >
          <Copy size={11} />
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            actions.removeRow(slideId, row.id);
          }}
          className="grid h-6 w-6 place-items-center rounded-md border border-border bg-white text-fg-subtle shadow-pill hover:text-red-500"
          title="Delete row"
        >
          <Trash2 size={11} />
        </button>
      </div>

      {!multiColumn ? (
        <FreeformRow row={row} slideId={slideId} />
      ) : (
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex w-full min-w-0 items-start gap-3">
            {row.columns.map((c) => (
              <div key={c.id} className="min-w-0" style={{ width: `${c.widthPercent}%` }}>
                <FreeformColumnSurface
                  slideId={slideId}
                  row={row}
                  column={c}
                  forcedHeightPx={sharedHeightPx}
                />
              </div>
            ))}
          </div>
          <RowSharedHeightResizeBar
            slideId={slideId}
            row={row}
            heightPx={sharedHeightPx}
            onLiveHeight={setLiveRowHeightPx}
          />
        </div>
      )}
    </div>
  );
}
